// SPDX-License-Identifier: AGPL-3.0-or-later

import type {WalkInput} from '@app/features/voice_scene/scene/avatar/LocalWalker';

// What typing owns: the letters go there, never to walking.
const TYPING_SELECTOR = 'input, textarea, select, [contenteditable]';
const MODAL_SELECTOR = 'dialog[open], [role="dialog"][aria-modal="true"], [role="alertdialog"][aria-modal="true"]';
const AXES: Readonly<Record<string, {forward?: number; right?: number}>> = {
	KeyW: {forward: 1},
	KeyS: {forward: -1},
	KeyD: {right: 1},
	KeyA: {right: -1},
};
// The room chat sits in the room view, and its message list has keys of its own.
const ROOM_CHAT_SELECTOR = '[data-room-chat]';

// The focus is in the room view (`scope`) and on nothing there for typing.
export function roomHasFocus(scope: Element): boolean {
	const active = scope.ownerDocument.activeElement;
	return (
		active !== null && scope.contains(active) && !active.closest(TYPING_SELECTOR) && !active.closest(ROOM_CHAT_SELECTOR)
	);
}

// A key for the room view (its walk or its camera): the focus is there, and no Ctrl, Cmd or Alt (their shortcuts stay
// the app's) and no composition.
export function isRoomKeyEvent(event: KeyboardEvent, scope: Element): boolean {
	if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing) return false;
	return roomHasFocus(scope);
}

// A modal is open: keys are its own.
export function hasOpenModal(doc: Document): boolean {
	return doc.querySelector(MODAL_SELECTOR) !== null;
}

export interface RoomInput {
	axes(): WalkInput;
	// Whether any walking key is down.
	held(): boolean;
	dispose(): void;
}

export interface RoomInputOptions {
	canvas: HTMLCanvasElement;
	// The room view around the canvas (its overlay and toolbar): focus anywhere in it counts as on the room.
	scope: Element;
	// A walking key went down.
	onMove(): void;
	// E went down: sit on the seat in reach, or stand up.
	onInteract(): void;
}

// The walking keys, by their place on the keyboard (event.code), only while the room view has the focus and nothing
// in it is for typing, and never with Ctrl, Cmd or Alt (their shortcuts stay the app's); a key it takes goes no further. A press on the canvas gives
// it the focus (focusable while the input lives, unless it was already). Focus that fell to the page out of the room (a
// control went away or turned disabled, a field let go) comes back to the canvas with a walking key or E; what was
// last pressed or focused tells the room from the rest of the app, whose keys stay its own. Losing the window or hiding
// the tab lets every key go, as does a key up anywhere.
export function createRoomInput({canvas, scope, onMove, onInteract}: RoomInputOptions): RoomInput {
	const doc = canvas.ownerDocument;
	const win = doc.defaultView ?? window;
	const held = new Set<string>();
	let run = false;
	let roomOwns = false;
	const reclaimFocus = (event: KeyboardEvent): void => {
		if (!AXES[event.code] && event.code !== 'KeyE') return;
		if (!roomOwns || doc.activeElement !== doc.body) return;
		if (event.ctrlKey || event.metaKey || event.altKey || event.isComposing || hasOpenModal(doc)) return;
		canvas.focus({preventScroll: true});
	};
	const onKeyDown = (event: KeyboardEvent): void => {
		reclaimFocus(event);
		if (!isRoomKeyEvent(event, scope)) return;
		if (event.code === 'KeyE') {
			event.preventDefault();
			event.stopPropagation();
			if (!event.repeat) onInteract();
			return;
		}
		if (!AXES[event.code]) return;
		event.preventDefault();
		event.stopPropagation();
		run = event.shiftKey;
		held.add(event.code);
		onMove();
	};
	const onKeyUp = (event: KeyboardEvent): void => {
		held.delete(event.code);
		run = event.shiftKey;
	};
	const release = (): void => {
		held.clear();
		run = false;
	};
	const onVisibility = (): void => {
		if (doc.hidden) release();
	};
	const onFocusIn = (): void => {
		roomOwns = scope.contains(doc.activeElement);
		if (!roomHasFocus(scope)) release();
	};
	const onPointerDown = (): void => canvas.focus({preventScroll: true});
	const onPressAnywhere = (event: PointerEvent): void => {
		roomOwns = scope.contains(event.target as Node);
	};
	const hadTabIndex = canvas.hasAttribute('tabindex');
	if (!hadTabIndex) canvas.tabIndex = 0;
	canvas.addEventListener('pointerdown', onPointerDown);
	// Capture: ahead of the window's own listeners (a channel composer's type-anywhere would take the letter and the focus).
	win.addEventListener('keydown', onKeyDown, true);
	win.addEventListener('keyup', onKeyUp);
	win.addEventListener('blur', release);
	doc.addEventListener('focusin', onFocusIn);
	doc.addEventListener('pointerdown', onPressAnywhere, true);
	doc.addEventListener('visibilitychange', onVisibility);
	return {
		axes() {
			let forward = 0;
			let right = 0;
			for (const code of held) {
				forward += AXES[code]?.forward ?? 0;
				right += AXES[code]?.right ?? 0;
			}
			return {forward, right, run: run && held.size > 0};
		},
		held: () => held.size > 0,
		dispose() {
			release();
			if (!hadTabIndex) canvas.removeAttribute('tabindex');
			canvas.removeEventListener('pointerdown', onPointerDown);
			win.removeEventListener('keydown', onKeyDown, true);
			win.removeEventListener('keyup', onKeyUp);
			win.removeEventListener('blur', release);
			doc.removeEventListener('focusin', onFocusIn);
			doc.removeEventListener('pointerdown', onPressAnywhere, true);
			doc.removeEventListener('visibilitychange', onVisibility);
		},
	};
}
